import _ from 'lodash';
import { createSelector } from 'reselect';

import { getUserRights } from '../../form-designer/selectors/common.selectors';
import { difference } from '../../form-designer/utils/editor.utils';
import {
  EDIT_RIGHT,
  ENTITY_RELEASE_TYPE_PILGRIM,
  ENTITY_STATUS_INWORKS,
  ENTITY_STATUS_NONE,
  ENTITY_STATUS_PUBLISHED,
  ENTITY_TYPE_CHILD,
  ENTITY_TYPE_MASTER_SETUP_CHILD,
  ENTITY_TYPE_MASTER_SETUP_PARENT,
  ENTITY_TYPE_PARENT,
  ENTITY_VERSION_ONE,
  RELATION_TYPES,
  SPECIALIZED_RIGHT,
  VIEW_RIGHT,
} from '../constants/entity-designer.constants';
import {
  getRelationTypeLabel,
  modifyEntityFields,
  modifyFieldSetListItems,
  modifyTargetListItems,
} from '../utils/entity-designer.utils';

export const getEntityModel = (state) => state.entityEditor?.entityModel ?? {};
export const getEntityOriginalModel = (state) => state.entityEditor?.originalEntityModel ?? {};
export const getEntityInfo = (state) => state.entityEditor?.entityModel?.EntityInfo ?? {};
export const getCurrentEntityName = (state) =>
  state.entityEditor?.entityModel?.EntityInfo?.EntityName ?? '';
export const getEntityFields = (state) => state.entityEditor?.entityModel?.EntityFields ?? [];
export const getSelectedFieldSet = (state) => state.entityEditor?.selectedFieldSet ?? '';
export const getRelatedEntities = (state) =>
  state.entityEditor?.entityModel?.EntityInfo?.RelatedEntities ?? [];
export const getResolvedFields = (state) => state.entityEditor?.resolvedFields ?? {};
export const getValidationErrors = (state) => state.entityEditor?.validationErrors ?? [];

export const getIsImportMode = (state) => !!state.entityEditor?.isImportMode;

export const getHasSpecializedRight = (state) => state.userRights?.includes(SPECIALIZED_RIGHT);

export const getIsEntityModelLoaded = (state) => !_.isEmpty(state.entityEditor?.entityModel);

export const getIsLabelDisabled = createSelector(
  [getEntityInfo],
  ({ Version, Status }) =>
    Version > ENTITY_VERSION_ONE || ![ENTITY_STATUS_INWORKS, ENTITY_STATUS_NONE].includes(Status)
);

export const getEntityProp = createSelector(
  [getEntityInfo, getIsLabelDisabled, (state, props) => props],
  (entityInfo, isLabelDisabled, { prop }) => {
    if (prop === 'Label' && isLabelDisabled) {
      return entityInfo[prop] || entityInfo.EntityName;
    }
    return entityInfo[prop];
  }
);

export const getIsRuntimeTableDropdownDisabled = createSelector(
  [getEntityInfo],
  ({ Version, Status, IsGenericTable }) =>
    Version > ENTITY_VERSION_ONE ||
    ![ENTITY_STATUS_INWORKS, ENTITY_STATUS_NONE].includes(Status) ||
    !IsGenericTable
);

export const getEntityType = createSelector([getEntityInfo], (entityInfo) =>
  entityInfo.IsParent && !entityInfo.IsMasterSetup
    ? ENTITY_TYPE_PARENT
    : !entityInfo.IsParent && !entityInfo.IsMasterSetup
    ? ENTITY_TYPE_CHILD
    : entityInfo.IsParent && entityInfo.IsMasterSetup
    ? ENTITY_TYPE_MASTER_SETUP_PARENT
    : !entityInfo.IsParent && entityInfo.IsMasterSetup
    ? ENTITY_TYPE_MASTER_SETUP_CHILD
    : null
);

export const getIsViewOnlyMode = createSelector([getUserRights], (userRights) => {
  const hasViewRight = userRights.includes(VIEW_RIGHT);
  const hasEditRight = userRights.includes(EDIT_RIGHT);

  if (!hasViewRight && !hasEditRight) {
    return true;
  }

  return hasViewRight && !hasEditRight;
});

export const getEditMode = createSelector(
  [getEntityInfo, getIsViewOnlyMode, (state, props) => props],
  (entityInfo, isViewOnlyMode, props) =>
    !isViewOnlyMode &&
    [ENTITY_STATUS_INWORKS, ENTITY_STATUS_PUBLISHED, ENTITY_STATUS_NONE].includes(
      entityInfo.Status
    ) &&
    !props?.isEditMode
);

export const getRelations = createSelector(
  [getRelatedEntities, getValidationErrors],
  (relations, validationErrors) =>
    relations.map((relation) => {
      const processedRelation = Object.keys(relation).reduce((acc, key) => {
        acc[key] = relation[key] ?? '';
        return acc;
      }, {});

      return {
        ...processedRelation,
        errors: validationErrors.filter((error) => error.instanceId === relation.rowId),
        RelationTypeLabel: getRelationTypeLabel({
          type: relation.RelationType,
          types: RELATION_TYPES,
        }),
      };
    })
);

export const getSaveDisabled = createSelector(
  [getEntityModel, getEntityOriginalModel, getEditMode],
  (entityModel, originalEntityModel, isEditMode) => {
    if (!isEditMode) {
      return true;
    }

    const entityStatus = entityModel.EntityInfo.Status;

    if (entityStatus === ENTITY_STATUS_NONE) {
      return false;
    }

    const hasChanges = difference(entityModel, originalEntityModel);

    return _.isEmpty(hasChanges);
  }
);

export const getPublishDisabled = createSelector(
  [getEntityInfo, getSaveDisabled, getEditMode],
  (entityInfo, savedDisabled, isEditMode) => {
    if (!isEditMode) {
      return true;
    }

    const isPilgrim = entityInfo.ReleaseType === ENTITY_RELEASE_TYPE_PILGRIM;
    const isPublished = entityInfo.Status === ENTITY_STATUS_PUBLISHED;

    return isPilgrim || isPublished || !savedDisabled;
  }
);

export const hasEntityUnsavedChanges = createSelector(
  [getSaveDisabled],
  (saveDisabled) => !saveDisabled
);

export const getResolvedFieldsByFieldSet = createSelector(
  [getSelectedFieldSet, getResolvedFields, getEntityFields, getValidationErrors],
  (selectedFieldSet, resolvedFields, fields, validationErrors) => {
    if (!selectedFieldSet) {
      return [];
    }
    const resolvedFieldsByFieldSet =
      resolvedFields.filter((field) => field.fieldSetId === selectedFieldSet) ?? [];

    return resolvedFieldsByFieldSet.map((resolvedField) => ({
      ...resolvedField,
      errors: validationErrors.filter((error) => error.instanceId === resolvedField.rowId),
      targetListItems: modifyTargetListItems(
        fields
          .filter(
            (field) =>
              field.rowId === resolvedField.target ||
              (!field.ResolverType && !field.FieldSet && field.FieldName && !field.IsComputed)
          )
          .sort((a, b) => a.FieldName?.localeCompare(b.FieldName))
      ),
      targetFieldName:
        fields.find((field) => resolvedField.target === field.rowId)?.FieldName || '',
    }));
  }
);

export const getFieldSetListItems = createSelector([getEntityFields], (fields) => {
  const items = fields.filter((field) => field.ResolverType && field.FieldName);
  return modifyFieldSetListItems(items);
});

export const getSelectedFieldSetInfo = createSelector(
  [getSelectedFieldSet, getEntityFields],
  (selectedFieldSet, fields) => fields.find((field) => field.rowId === selectedFieldSet) ?? {}
);

export const getFields = createSelector(
  [getEntityFields, getResolvedFields, getValidationErrors, (state, props) => props],
  (fields, resolvedFields, validationErrors, { fieldTypes }) =>
    modifyEntityFields({ fields, fieldTypes, resolvedFields, validationErrors })
);
